import { useParams, Link, useNavigate } from 'react-router-dom'
import { format } from 'date-fns'
import { th } from 'date-fns/locale'
import { Card } from '@/common/ui/card'
import useQueryWithdrawTransactionsById from '../../api/hooks/useQueryWithdrawTransactionsById'
import { cn } from '@/common/lib/utils'
import { WithdrawStatus } from '@/schema'
import { Icons } from '@/common/ui/icons'
import { Separator } from '@/common/ui/separator'
import { Button } from '@/common/ui/button'
import {
	ArrowLeft,
	CheckCircle,
	AlertTriangle,
	Clock,
	Eye,
	EyeOff,
	X,
	RotateCcw,
	RefreshCw,
} from 'lucide-react'
import { useState } from 'react'
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
	AlertDialogTrigger,
} from '@/common/ui/alert-dialog'
import { Badge } from '@/common/ui/badge'
import { useMutateWithdrawTransaction } from '../../api/hooks/useMutateWithdrawTransaction'
import useQueryProfile from '@/modules/auth/api/hooks/useQueryProfile'
import { Textarea } from '@/common/ui/textarea'
import { Label } from '@/common/ui/label'
import { PermissionKeys } from '@/modules/manage-member/mappers'
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/common/ui/table'

export default function WithdrawByIdListing() {
	const { id } = useParams<{ id: string }>()
	const navigate = useNavigate()
	const { data: withdraw, isLoading } = useQueryWithdrawTransactionsById(id!)
	const { data: user } = useQueryProfile()
	const { approveWithdraw, cancel, refund, retryWithdraw } =
		useMutateWithdrawTransaction()
	const [showTransactionId, setShowTransactionId] = useState(false)
	const [cancelNote, setCancelNote] = useState('')
	const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false)
	const [refundNote, setRefundNote] = useState('')
	const [isRefundDialogOpen, setIsRefundDialogOpen] = useState(false)
	const [isRetryDialogOpen, setIsRetryDialogOpen] = useState(false)

	const hasWritePermission = user?.userGroup.writePermission.includes(
		PermissionKeys.MANAGE_BANK_TRANSACTION,
	)

	const formatDate = (date: string) => {
		return format(new Date(date), 'PPP HH:mm น.', { locale: th })
	}

	const getStatusText = (status: string) => {
		const statusMap: Record<string, string> = {
			PENDING: 'รอดำเนินการ',
			PROCESSING: 'กำลังดำเนินการ',
			COMPLETED: 'สำเร็จ',
			FAILED: 'ไม่สำเร็จ',
			CANCELED: 'ยกเลิก',
			EXPIRED: 'หมดอายุ',
			REFUNDED: 'คืนเงินแล้ว',
		}
		return statusMap[status] || status
	}

	const getStatusColor = (status: WithdrawStatus) => {
		const statusColors: Record<WithdrawStatus, string> = {
			PENDING: 'text-yellow-500',
			PROCESSING: 'text-yellow-500',
			COMPLETED: 'text-green-500',
			FAILED: 'text-red-500',
			CANCELED: 'text-red-500',
			EXPIRED: 'text-gray-500',
			REFUNDED: 'text-orange-500',
		}
		return statusColors[status]
	}

	const handleApproveWithdraw = (isManual: boolean) => {
		if (id) {
			approveWithdraw.mutate({ id, isManual })
		}
	}

	const handleCancelWithdraw = () => {
		if (id) {
			cancel.mutate({ id, note: cancelNote || undefined })
			setIsCancelDialogOpen(false)
			setCancelNote('')
		}
	}

	const handleRefundWithdraw = () => {
		if (id) {
			refund.mutate({ id, note: refundNote || undefined })
			setIsRefundDialogOpen(false)
			setRefundNote('')
		}
	}

	const handleRetryWithdraw = () => {
		if (id) {
			retryWithdraw.mutate(id)
			setIsRetryDialogOpen(false)
		}
	}

	const canApprove =
		hasWritePermission &&
		(withdraw?.status === 'PENDING' || withdraw?.status === 'EXPIRED') &&
		withdraw?.processor === 'MANUAL'

	const canRefund =
		hasWritePermission &&
		(withdraw?.status === 'FAILED' || withdraw?.status === 'EXPIRED') &&
		!withdraw?.timestamp?.['REFUND']

	if (isLoading)
		return (
			<div className="flex h-screen items-center justify-center">
				<div className="flex items-center gap-2">
					<Icons.spinner className="h-5 w-5 animate-spin" />
					<span className="text-lg">กำลังโหลด...</span>
				</div>
			</div>
		)

	if (!withdraw) {
		return (
			<div className="flex h-screen items-center justify-center">
				<div className="flex items-center gap-2">
					<Icons.warning className="h-5 w-5 text-yellow-500" />
					<span className="text-lg">ไม่พบข้อมูล</span>
				</div>
			</div>
		)
	}

	return (
		<div className="p-6">
			<div className="mb-6 flex items-center justify-between">
				<h1 className="text-2xl font-bold">รายละเอียดการถอนเงิน</h1>
				<div className="flex items-center gap-3">
					{/* Action Buttons */}
					{canApprove && (
						<div className="flex items-center gap-2">
							{/* Auto Approval Button */}
							<AlertDialog>
								<AlertDialogTrigger asChild>
									<Button
										variant="default"
										className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700"
										disabled={approveWithdraw.isPending}
									>
										{approveWithdraw.isPending ? (
											<Icons.spinner className="h-4 w-4 animate-spin" />
										) : (
											<CheckCircle className="h-4 w-4" />
										)}
										อนุมัติแบบออโต้
									</Button>
								</AlertDialogTrigger>
								<AlertDialogContent className="max-w-md">
									<AlertDialogHeader>
										<div className="flex items-center gap-2">
											<AlertTriangle className="h-5 w-5 text-blue-500" />
											<AlertDialogTitle className="text-lg">
												ยืนยันการอนุมัติการถอนเงินแบบออโต้
											</AlertDialogTitle>
										</div>
										<AlertDialogDescription className="space-y-3 text-sm">
											<div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
												<div className="flex items-start gap-2">
													<AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-blue-500" />
													<div className="space-y-1">
														<p className="font-medium text-blue-800">
															การดำเนินการนี้จะส่งคำสั่งไปยังธนาคารผ่าน API
														</p>
														<p className="text-blue-700">
															เมื่อกดยืนยัน ระบบจะทำการโอนเงินจำนวน{' '}
															<span className="font-semibold">
																฿{withdraw.amount?.toLocaleString()}
															</span>{' '}
															ไปยังบัญชี {withdraw.playerBank?.bankProvider}{' '}
															{withdraw.playerBank?.accountNumber}{' '}
															ผ่านระบบธนาคารโดยอัตโนมัติ
														</p>
													</div>
												</div>
											</div>
											<div className="space-y-2">
												<div className="flex justify-between text-sm">
													<span>จำนวนเงิน:</span>
													<span className="font-semibold text-red-600">
														฿{withdraw.amount?.toLocaleString()}
													</span>
												</div>
												<div className="flex justify-between text-sm">
													<span>บัญชีปลายทาง:</span>
													<span className="font-medium">
														{withdraw.playerBank?.bankProvider}{' '}
														{withdraw.playerBank?.accountNumber}
													</span>
												</div>
												<div className="flex justify-between text-sm">
													<span>ชื่อบัญชี:</span>
													<span className="font-medium">
														{withdraw.playerBank?.accountName}
													</span>
												</div>
											</div>
											<p className="text-xs text-gray-600">
												⚠️
												การดำเนินการนี้ไม่สามารถยกเลิกได้หลังจากที่ส่งคำสั่งไปยังธนาคารแล้ว
											</p>
										</AlertDialogDescription>
									</AlertDialogHeader>
									<AlertDialogFooter>
										<AlertDialogCancel>ยกเลิก</AlertDialogCancel>
										<AlertDialogAction
											onClick={() => handleApproveWithdraw(false)}
											className="bg-blue-600 hover:bg-blue-700"
										>
											<CheckCircle className="mr-2 h-4 w-4" />
											ยืนยันอนุมัติแบบออโต้
										</AlertDialogAction>
									</AlertDialogFooter>
								</AlertDialogContent>
							</AlertDialog>

							{/* Manual Approval Button */}
							<AlertDialog>
								<AlertDialogTrigger asChild>
									<Button
										variant="default"
										className="flex items-center gap-2 bg-green-600 hover:bg-green-700"
										disabled={approveWithdraw.isPending}
									>
										{approveWithdraw.isPending ? (
											<Icons.spinner className="h-4 w-4 animate-spin" />
										) : (
											<CheckCircle className="h-4 w-4" />
										)}
										อนุมัติแบบแมนนวล
									</Button>
								</AlertDialogTrigger>
								<AlertDialogContent className="max-w-md">
									<AlertDialogHeader>
										<div className="flex items-center gap-2">
											<AlertTriangle className="h-5 w-5 text-amber-500" />
											<AlertDialogTitle className="text-lg">
												ยืนยันการอนุมัติการถอนเงินแบบแมนนวล
											</AlertDialogTitle>
										</div>
										<AlertDialogDescription className="space-y-3 text-sm">
											<div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
												<div className="flex items-start gap-2">
													<AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-500" />
													<div className="space-y-1">
														<p className="font-medium text-amber-800">
															กรุณาทำการโอนเงินด้วยตนเองก่อนอนุมัติ
														</p>
														<p className="text-amber-700">
															เมื่อกดยืนยัน ระบบจะทำเครื่องหมายเงินจำนวน{' '}
															<span className="font-semibold">
																฿{withdraw.amount?.toLocaleString()}
															</span>{' '}
															เป็นสำเร็จโดยไม่ส่งคำสั่งไปยังธนาคาร
														</p>
													</div>
												</div>
											</div>
											<div className="space-y-2">
												<div className="flex justify-between text-sm">
													<span>จำนวนเงิน:</span>
													<span className="font-semibold text-red-600">
														฿{withdraw.amount?.toLocaleString()}
													</span>
												</div>
												<div className="flex justify-between text-sm">
													<span>บัญชีปลายทาง:</span>
													<span className="font-medium">
														{withdraw.playerBank?.bankProvider}{' '}
														{withdraw.playerBank?.accountNumber}
													</span>
												</div>
												<div className="flex justify-between text-sm">
													<span>ชื่อบัญชี:</span>
													<span className="font-medium">
														{withdraw.playerBank?.accountName}
													</span>
												</div>
											</div>
											<p className="text-xs text-gray-600">
												⚠️ กรุณาทำการโอนเงินก่อนกดยืนยันอนุมัติแบบแมนนวล
											</p>
										</AlertDialogDescription>
									</AlertDialogHeader>
									<AlertDialogFooter>
										<AlertDialogCancel>ยกเลิก</AlertDialogCancel>
										<AlertDialogAction
											onClick={() => handleApproveWithdraw(true)}
											className="bg-green-600 hover:bg-green-700"
										>
											<CheckCircle className="mr-2 h-4 w-4" />
											ยืนยันอนุมัติแบบแมนนวล
										</AlertDialogAction>
									</AlertDialogFooter>
								</AlertDialogContent>
							</AlertDialog>

							{/* Cancel Button */}
							<AlertDialog
								open={isCancelDialogOpen}
								onOpenChange={setIsCancelDialogOpen}
							>
								<AlertDialogTrigger asChild>
									<Button
										variant="destructive"
										className="flex items-center gap-2"
										disabled={cancel.isPending}
									>
										{cancel.isPending ? (
											<Icons.spinner className="h-4 w-4 animate-spin" />
										) : (
											<X className="h-4 w-4" />
										)}
										ยกเลิก
									</Button>
								</AlertDialogTrigger>
								<AlertDialogContent className="max-w-md">
									<AlertDialogHeader>
										<div className="flex items-center gap-2">
											<X className="h-5 w-5 text-red-500" />
											<AlertDialogTitle className="text-lg">
												ยืนยันการยกเลิกการถอนเงิน
											</AlertDialogTitle>
										</div>
										<AlertDialogDescription className="space-y-3 text-sm">
											<div className="rounded-lg border border-red-200 bg-red-50 p-3">
												<div className="flex items-start gap-2">
													<AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-red-500" />
													<div className="space-y-1">
														<p className="font-medium text-red-800">
															การดำเนินการนี้จะยกเลิกการถอนเงิน
														</p>
														<p className="text-red-700">
															เมื่อกดยืนยัน ระบบจะยกเลิกคำขอถอนเงินจำนวน{' '}
															<span className="font-semibold">
																฿{withdraw.amount?.toLocaleString()}
															</span>
														</p>
													</div>
												</div>
											</div>
											<div className="space-y-2">
												<div className="flex justify-between text-sm">
													<span>จำนวนเงิน:</span>
													<span className="font-semibold text-red-600">
														฿{withdraw.amount?.toLocaleString()}
													</span>
												</div>
												<div className="flex justify-between text-sm">
													<span>บัญชีปลายทาง:</span>
													<span className="font-medium">
														{withdraw.playerBank?.bankProvider}{' '}
														{withdraw.playerBank?.accountNumber}
													</span>
												</div>
												<div className="flex justify-between text-sm">
													<span>ชื่อบัญชี:</span>
													<span className="font-medium">
														{withdraw.playerBank?.accountName}
													</span>
												</div>
											</div>
											<div className="space-y-2">
												<Label
													htmlFor="cancel-note"
													className="text-sm font-medium"
												>
													หมายเหตุ (ถ้ามี)
												</Label>
												<Textarea
													id="cancel-note"
													placeholder="ระบุเหตุผลในการยกเลิก..."
													value={cancelNote}
													onChange={(e) => setCancelNote(e.target.value)}
													className="min-h-[80px]"
												/>
											</div>
											<p className="text-xs text-gray-600">
												⚠️ การยกเลิกนี้ไม่สามารถย้อนกลับได้
											</p>
										</AlertDialogDescription>
									</AlertDialogHeader>
									<AlertDialogFooter>
										<AlertDialogCancel>ยกเลิก</AlertDialogCancel>
										<AlertDialogAction
											onClick={handleCancelWithdraw}
											className="bg-red-600 hover:bg-red-700"
										>
											<X className="mr-2 h-4 w-4" />
											ยืนยันยกเลิก
										</AlertDialogAction>
									</AlertDialogFooter>
								</AlertDialogContent>
							</AlertDialog>
						</div>
					)}

					{/* Retry & Refund Actions - for FAILED/EXPIRED withdrawals (only if not already refunded) */}
					{canRefund && (
						<>
							<AlertDialog
								open={isRetryDialogOpen}
								onOpenChange={setIsRetryDialogOpen}
							>
								<AlertDialogTrigger asChild>
									<Button
										variant="outline"
										className="flex items-center gap-2 border-blue-300 bg-blue-50 text-blue-700 hover:bg-blue-100"
										disabled={retryWithdraw.isPending}
									>
										{retryWithdraw.isPending ? (
											<Icons.spinner className="h-4 w-4 animate-spin" />
										) : (
											<RefreshCw className="h-4 w-4" />
										)}
										ลองอีกครั้ง
									</Button>
								</AlertDialogTrigger>
								<AlertDialogContent className="max-w-md">
									<AlertDialogHeader>
										<div className="flex items-center gap-2">
											<RefreshCw className="h-5 w-5 text-blue-500" />
											<AlertDialogTitle className="text-lg">
												ยืนยันลองอีกครั้ง
											</AlertDialogTitle>
										</div>
										<AlertDialogDescription className="space-y-3 text-sm">
											<div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
												<div className="flex items-start gap-2">
													<AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-blue-500" />
													<div className="space-y-1">
														<p className="font-medium text-blue-800">
															การดำเนินการนี้จะส่งคำสั่งโอนเงินไปยังธนาคารอีกครั้ง
														</p>
														<p className="text-blue-700">
															เมื่อกดยืนยัน ระบบจะดำเนินการโอนเงินจำนวน{' '}
															<span className="font-semibold">
																฿{withdraw.amount?.toLocaleString()}
															</span>{' '}
															ไปยังบัญชี {withdraw.playerBank?.bankProvider}{' '}
															{withdraw.playerBank?.accountNumber}{' '}
															ผ่านระบบธนาคารอีกครั้ง
														</p>
													</div>
												</div>
											</div>
											<div className="space-y-2">
												<div className="flex justify-between text-sm">
													<span>จำนวนเงิน:</span>
													<span className="font-semibold text-red-600">
														฿{withdraw.amount?.toLocaleString()}
													</span>
												</div>
												<div className="flex justify-between text-sm">
													<span>บัญชีปลายทาง:</span>
													<span className="font-medium">
														{withdraw.playerBank?.bankProvider}{' '}
														{withdraw.playerBank?.accountNumber}
													</span>
												</div>
												<div className="flex justify-between text-sm">
													<span>ชื่อบัญชี:</span>
													<span className="font-medium">
														{withdraw.playerBank?.accountName}
													</span>
												</div>
											</div>
										</AlertDialogDescription>
									</AlertDialogHeader>
									<AlertDialogFooter>
										<AlertDialogCancel>ยกเลิก</AlertDialogCancel>
										<AlertDialogAction
											onClick={handleRetryWithdraw}
											className="bg-blue-600 hover:bg-blue-700"
										>
											<RefreshCw className="mr-2 h-4 w-4" />
											ยืนยันลองอีกครั้ง
										</AlertDialogAction>
									</AlertDialogFooter>
								</AlertDialogContent>
							</AlertDialog>
							<AlertDialog
								open={isRefundDialogOpen}
								onOpenChange={setIsRefundDialogOpen}
							>
								<AlertDialogTrigger asChild>
									<Button
										variant="outline"
										className="flex items-center gap-2 border-orange-300 bg-orange-50 text-orange-700 hover:bg-orange-100"
										disabled={refund.isPending}
									>
										{refund.isPending ? (
											<Icons.spinner className="h-4 w-4 animate-spin" />
										) : (
											<RotateCcw className="h-4 w-4" />
										)}
										คืนเงิน
									</Button>
								</AlertDialogTrigger>
								<AlertDialogContent className="max-w-md">
									<AlertDialogHeader>
										<div className="flex items-center gap-2">
											<RotateCcw className="h-5 w-5 text-orange-500" />
											<AlertDialogTitle className="text-lg">
												ยืนยันการคืนเงินถอน
											</AlertDialogTitle>
										</div>
										<AlertDialogDescription className="space-y-3 text-sm">
											<div className="rounded-lg border border-orange-200 bg-orange-50 p-3">
												<div className="flex items-start gap-2">
													<AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-orange-500" />
													<div className="space-y-1">
														<p className="font-medium text-orange-800">
															การดำเนินการนี้จะคืนเงินกลับไปยังกระเป๋าผู้เล่น
														</p>
														<p className="text-orange-700">
															เมื่อกดยืนยัน ระบบจะคืนเงินจำนวน{' '}
															<span className="font-semibold">
																฿{withdraw.amount?.toLocaleString()}
															</span>{' '}
															กลับไปยังกระเป๋าของผู้เล่น
														</p>
													</div>
												</div>
											</div>
											<div className="space-y-2">
												<div className="flex justify-between text-sm">
													<span>จำนวนเงินที่จะคืน:</span>
													<span className="font-semibold text-green-600">
														฿{withdraw.amount?.toLocaleString()}
													</span>
												</div>
												<div className="flex justify-between text-sm">
													<span>บัญชีปลายทางเดิม:</span>
													<span className="font-medium">
														{withdraw.playerBank?.bankProvider}{' '}
														{withdraw.playerBank?.accountNumber}
													</span>
												</div>
												<div className="flex justify-between text-sm">
													<span>ชื่อบัญชี:</span>
													<span className="font-medium">
														{withdraw.playerBank?.accountName}
													</span>
												</div>
											</div>
											<div className="space-y-2">
												<Label
													htmlFor="refund-note"
													className="text-sm font-medium"
												>
													หมายเหตุ (ถ้ามี)
												</Label>
												<Textarea
													id="refund-note"
													placeholder="ระบุเหตุผลในการคืนเงิน..."
													value={refundNote}
													onChange={(e) => setRefundNote(e.target.value)}
													className="min-h-[80px]"
												/>
											</div>
											<p className="text-xs text-gray-600">
												⚠️ การคืนเงินนี้ไม่สามารถย้อนกลับได้
											</p>
										</AlertDialogDescription>
									</AlertDialogHeader>
									<AlertDialogFooter>
										<AlertDialogCancel>ยกเลิก</AlertDialogCancel>
										<AlertDialogAction
											onClick={handleRefundWithdraw}
											className="bg-orange-600 hover:bg-orange-700"
										>
											<RotateCcw className="mr-2 h-4 w-4" />
											ยืนยันคืนเงิน
										</AlertDialogAction>
									</AlertDialogFooter>
								</AlertDialogContent>
							</AlertDialog>
						</>
					)}

					<Button
						onClick={() => navigate(-1)}
						variant="outline"
						className="flex items-center gap-2 rounded-md border px-4 py-2 hover:bg-gray-100"
					>
						<ArrowLeft className="h-4 w-4" />
						ย้อนกลับ
					</Button>
				</div>
			</div>

			{/* Status Alert Banner */}
			{withdraw.status === 'PENDING' && hasWritePermission && (
				<div className="mb-6">
					<div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
						<div className="flex items-start gap-3">
							<Clock className="mt-0.5 h-5 w-5 flex-shrink-0 text-blue-500" />
							<div className="flex-1">
								<div className="flex items-center justify-between">
									<div>
										<h3 className="font-medium text-blue-900">
											รอการอนุมัติการถอนเงิน
										</h3>
										<p className="mt-1 text-sm text-blue-700">
											รายการนี้รอการอนุมัติเพื่อดำเนินการโอนเงินผ่านระบบธนาคาร
										</p>
									</div>
									<Badge
										variant="secondary"
										className="bg-blue-100 text-blue-800"
									>
										รอดำเนินการ
									</Badge>
								</div>
							</div>
						</div>
					</div>
				</div>
			)}

			<div className="grid gap-6 md:grid-cols-2">
				{/* Transaction Info */}
				<Card className="col-span-2 overflow-hidden md:col-span-1">
					<div className="border-b bg-muted/30 p-6">
						<h2 className="flex items-center gap-2 text-xl font-semibold">
							<Icons.billing className="h-5 w-5" />
							ข้อมูลธุรกรรม
						</h2>
					</div>
					<div className="space-y-4 p-6">
						<div className="grid gap-4 md:grid-cols-2">
							<div className="flex items-center gap-3">
								<Icons.billing className="h-4 w-4 text-gray-500" />
								<div>
									<p className="text-sm text-gray-500">
										รหัสธุรกรรมอ้างอิงธนาคารออโต้
									</p>
									<p className="font-medium">{withdraw.bankTxId || '-'}</p>
								</div>
							</div>
							<div className="flex items-center gap-3">
								<Icons.billing className="h-4 w-4 text-gray-500" />
								<div className="flex-1">
									<p className="text-sm text-gray-500">รหัสธุรกรรมภายใน</p>
									<div className="flex items-center gap-2">
										<p className="font-medium">
											{showTransactionId ? id : '••••••••••••••••'}
										</p>
										<Button
											variant="ghost"
											size="sm"
											onClick={() => setShowTransactionId(!showTransactionId)}
											className="h-6 w-6 p-0 hover:bg-gray-100"
										>
											{showTransactionId ? (
												<EyeOff className="h-4 w-4 text-gray-500" />
											) : (
												<Eye className="h-4 w-4 text-gray-500" />
											)}
										</Button>
									</div>
								</div>
							</div>

							<div className="flex items-center gap-3">
								<Icons.billing className="h-4 w-4 text-gray-500" />
								<div>
									<p className="text-sm text-gray-500">จำนวนเงิน</p>
									<div className="flex items-center gap-2">
										<p className="text-lg font-semibold text-red-600">
											฿{withdraw.amount?.toLocaleString()}
										</p>
									</div>
								</div>
							</div>
							<div className="flex items-center gap-3">
								<Icons.check className="h-4 w-4 text-gray-500" />
								<div>
									<p className="text-sm text-gray-500">สถานะ</p>
									<div className="flex items-center gap-2">
										<div
											className={cn(
												'h-2.5 w-2.5 rounded-full',
												withdraw.status === 'COMPLETED' && 'bg-green-500',
												withdraw.status === 'PENDING' && 'bg-yellow-500',
												withdraw.status === 'PROCESSING' && 'bg-yellow-500',
												withdraw.status === 'FAILED' && 'bg-red-500',
												(withdraw.status === 'CANCELED' ||
													withdraw.status === 'EXPIRED') &&
													'bg-gray-500',
												withdraw.status === 'REFUNDED' && 'bg-orange-500',
											)}
										/>
										<p
											className={cn(
												'text-lg font-semibold',
												getStatusColor(withdraw.status),
											)}
										>
											{getStatusText(withdraw.status)}
										</p>
									</div>
								</div>
							</div>
						</div>
						<Separator />
						<div className="grid gap-4 md:grid-cols-2">
							<div className="flex items-center gap-3">
								<Icons.user className="h-4 w-4 text-gray-500" />
								<div>
									<p className="text-sm text-gray-500">ประเภท</p>
									<p className="font-medium">
										{withdraw.status === WithdrawStatus.EXPIRED
											? 'หมดอายุ'
											: withdraw.processor === 'AUTO'
												? 'ถอนอัตโนมัติ'
												: `ทำรายการโดยพนักงาน ${withdraw.manualWithdrawOperator?.username ? ` ${withdraw.manualWithdrawOperator.username}` : ''}`}
									</p>
								</div>
							</div>
							<div className="flex items-center gap-3">
								<Icons.billing className="h-4 w-4 text-gray-500" />
								<div>
									<p className="text-sm text-gray-500">บัญชีรับเงิน</p>
									<p className="font-medium">
										{withdraw.bank
											? `${withdraw.bank.bankProvider} ${withdraw.bank.accountNumber}`
											: '-'}
									</p>
								</div>
							</div>
							<div className="flex items-center gap-3">
								<Icons.add className="h-4 w-4 text-gray-500" />
								<div>
									<p className="text-sm text-gray-500">วันที่สร้าง</p>
									<p className="font-medium">
										{withdraw.createdAt && formatDate(withdraw.createdAt)}
									</p>
								</div>
							</div>
							<div className="flex items-center gap-3">
								<Icons.refresh className="h-4 w-4 text-gray-500" />
								<div>
									<p className="text-sm text-gray-500">อัพเดทล่าสุด</p>
									<p className="font-medium">
										{withdraw.updatedAt && formatDate(withdraw.updatedAt)}
									</p>
								</div>
							</div>
						</div>
					</div>
				</Card>

				{/* Player Info */}
				<Card className="col-span-2 overflow-hidden md:col-span-1">
					<div className="border-b bg-muted/30 p-6">
						<h2 className="flex items-center justify-between">
							<div className="flex items-center gap-2 text-xl font-semibold">
								<Icons.user className="h-5 w-5" />
								ข้อมูลผู้เล่น
							</div>
							<Link
								to={`/operator/get-player-list/player-detail/${withdraw.playerId}`}
								className="flex items-center gap-2 text-sm text-blue-500 hover:text-blue-600"
							>
								<span>ดูข้อมูลผู้เล่น</span>
								<Icons.arrowRight className="h-4 w-4" />
							</Link>
						</h2>
					</div>
					<div className="space-y-6 p-6">
						<div className="grid gap-3 md:grid-cols-2">
							<div className="rounded-lg border p-4">
								<div className="flex items-center gap-3">
									<Icons.user className="h-4 w-4 text-gray-500" />
									<div>
										<p className="text-sm text-gray-500">ผู้เล่น</p>
										<p className="font-medium">
											{withdraw.player?.username || withdraw.player?.phone}
										</p>
									</div>
								</div>
							</div>
							<div className="rounded-lg border p-4">
								<div className="flex items-center gap-3">
									<Icons.billing className="h-4 w-4 text-gray-500" />
									<div>
										<p className="text-sm text-gray-500">เลขบัญชี</p>
										<p className="font-medium">
											{withdraw.playerBank?.accountNumber}
										</p>
									</div>
								</div>
							</div>
							<div className="rounded-lg border p-4">
								<div className="flex items-center gap-3">
									<Icons.billing className="h-4 w-4 text-gray-500" />
									<div>
										<p className="text-sm text-gray-500">ธนาคาร</p>
										<p className="font-medium">
											{withdraw.playerBank?.bankProvider}
										</p>
									</div>
								</div>
							</div>
						</div>
					</div>
				</Card>

				{/* Status History */}
				<Card className="col-span-2 overflow-hidden md:col-span-1">
					<div className="border-b bg-muted/30 p-6">
						<h2 className="flex items-center gap-2 text-xl font-semibold">
							<Icons.refresh className="h-5 w-5" />
							ประวัติสถานะ
						</h2>
					</div>
					<div className="space-y-4 p-6">
						<div className="grid gap-4">
							{withdraw.timestamp &&
								Object.entries(withdraw.timestamp)
									.sort(
										([, a], [, b]) =>
											new Date(a).getTime() - new Date(b).getTime(),
									)
									.map(([status, time]) => (
										<div key={status} className="flex items-center gap-3">
											<div
												className={cn(
													'h-2.5 w-2.5 rounded-full',
													status === 'COMPLETED' && 'bg-green-500',
													status === 'PENDING' && 'bg-yellow-500',
													status === 'PROCESSING' && 'bg-yellow-500',
													status === 'FAILED' && 'bg-red-500',
													(status === 'CANCELED' || status === 'EXPIRED') &&
														'bg-gray-500',
													status === 'REFUNDED' && 'bg-orange-500',
												)}
											/>
											<div>
												<p
													className={cn(
														'font-medium',
														getStatusColor(status as WithdrawStatus),
													)}
												>
													{getStatusText(status)}
												</p>
												<p className="text-sm text-gray-500">
													{formatDate(time)}
												</p>
											</div>
										</div>
									))}
						</div>
					</div>
				</Card>

				{/* Error Message Card - Only show if there's an error message */}
				{withdraw.note && (
					<Card className="col-span-2 overflow-hidden">
						<div className="border-b bg-red-50 p-6">
							<h2 className="flex items-center gap-2 text-xl font-semibold text-red-800">
								<AlertTriangle className="h-5 w-5" />
								หมายเหตุการยกเลิก
							</h2>
						</div>
						<div className="p-6">
							<div className="rounded-lg border border-red-200 bg-red-50 p-4">
								<div className="flex items-start gap-3">
									<AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-500" />
									<div className="flex-1">
										<p className="mb-2 font-medium text-red-800">
											เหตุผลในการยกเลิก:
										</p>
										<p className="whitespace-pre-wrap text-red-700">
											{withdraw.note}
										</p>
									</div>
								</div>
							</div>
						</div>
					</Card>
				)}

				{/* Time Information */}
				<Card className="col-span-2 overflow-hidden md:col-span-1">
					<div className="border-b bg-muted/30 p-6">
						<h2 className="flex items-center gap-2 text-xl font-semibold">
							<Icons.billing className="h-5 w-5" />
							ข้อมูลเวลา
						</h2>
					</div>
					<div className="space-y-4 p-6">
						<div className="grid gap-4 md:grid-cols-2">
							<div className="flex items-center gap-3">
								<Icons.add className="h-4 w-4 text-gray-500" />
								<div>
									<p className="text-sm text-gray-500">วันที่สร้าง</p>
									<p className="font-medium">
										{withdraw.createdAt && formatDate(withdraw.createdAt)}
									</p>
								</div>
							</div>
							<div className="flex items-center gap-3">
								<Icons.refresh className="h-4 w-4 text-gray-500" />
								<div>
									<p className="text-sm text-gray-500">อัพเดทล่าสุด</p>
									<p className="font-medium">
										{withdraw.updatedAt && formatDate(withdraw.updatedAt)}
									</p>
								</div>
							</div>
						</div>
					</div>
				</Card>

				{/* Payment Gateway Errors Table */}
				{withdraw.paymentGatewayErrors &&
					withdraw.paymentGatewayErrors.length > 0 && (
						<Card className="mt-6 p-6">
							<h3 className="mb-4 text-lg font-semibold">สาเหตุ</h3>
							<div className="overflow-x-auto">
								<Table>
									<TableHeader>
										<TableRow>
											<TableHead>Error ID</TableHead>
											<TableHead>ธนาคารออโต้</TableHead>
											<TableHead>ข้อมูล</TableHead>
											<TableHead>วันที่สร้าง</TableHead>
										</TableRow>
									</TableHeader>
									<TableBody>
										{withdraw.paymentGatewayErrors.map((error) => (
											<TableRow key={error.id}>
												<TableCell className="font-mono text-xs">
													{error.id}
												</TableCell>
												<TableCell>
													{error.paymentGateway?.bankProviderName || '-'}
												</TableCell>
												<TableCell>{error.errorMessage}</TableCell>
												<TableCell>{formatDate(error.createdAt)}</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							</div>
						</Card>
					)}
			</div>
		</div>
	)
}
